import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Eye,
  EyeOff,
  Loader2,
  Sun,
  Moon,
  Laptop,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { login } from '@/store/actions/creators/authActions';
import { errorMessageToast } from '@/utils/common/helper';
import loginPageContentService from '@/services/loginPageContent';
import { useTheme } from '@/components/ThemeProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getFlag } from '@/components/flags';
import LogoLight from '@/assets/images/logo_smsreflex_light.png';
import LogoDark from '@/assets/images/logo_smsreflex_dark.png';

const loginSchema = z.object({
  username: z.string().trim().min(1, { message: 'Username is required' }),
  password: z.string().trim().min(1, { message: 'Password is required' }),
  keepSignedIn: z.boolean().default(false),
});

type LoginFormValues = z.infer<typeof loginSchema>;

type Bullet = { icon?: string; title?: string; desc?: string };
type LoginContent = {
  id: number;
  language: string;
  flagCode: string;
  languageLabel?: string;
  headline?: string;
  subtitle?: string;
  bullets?: Bullet[];
  ctaText?: string;
  displayOrder: number;
  isActive: boolean;
};

function resolveIcon(name?: string): LucideIcon {
  if (!name) return Sparkles;
  const map = LucideIcons as unknown as Record<string, LucideIcon>;
  return (map[name] as LucideIcon) ?? Sparkles;
}

function ThemeFlagBar({
  contents,
  currentLang,
  onLangChange,
}: {
  contents: LoginContent[];
  currentLang: string;
  onLangChange: (lang: string) => void;
}) {
  const { theme, setTheme } = useTheme();
  return (
    <div className="absolute right-4 top-4 z-20 flex items-center gap-2">
      <div className="flex items-center gap-2">
        {contents
          .filter((c) => c.isActive)
          .map((c) => {
            const Flag = getFlag(c.flagCode);
            const active = c.language === currentLang;
            return (
              <button
                key={c.language}
                type="button"
                aria-label={c.languageLabel ?? c.language}
                aria-pressed={active}
                onClick={() => onLangChange(c.language)}
                className={
                  'h-6 w-6 overflow-hidden rounded-full transition-all hover:scale-110 ' +
                  (active
                    ? 'ring-2 ring-offset-2 ring-primary scale-110'
                    : 'ring-1 ring-border opacity-70 hover:opacity-100')
                }
              >
                {/* The flag SVG fills the round button via preserveAspectRatio
                    'xMidYMid slice' set as default on the SVG components. */}
                <Flag className="h-full w-full" />
              </button>
            );
          })}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-full bg-card/90 backdrop-blur"
            aria-label="Theme"
          >
            {theme === 'dark' ? (
              <Moon className="h-4 w-4" />
            ) : theme === 'light' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Laptop className="h-4 w-4" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setTheme('light')}>
            <Sun className="mr-2 h-4 w-4" /> Light
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('dark')}>
            <Moon className="mr-2 h-4 w-4" /> Dark
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTheme('system')}>
            <Laptop className="mr-2 h-4 w-4" /> System
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function Login() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [contents, setContents] = useState<LoginContent[]>([]);

  // Fetch the multilingual marketing content on mount.
  useEffect(() => {
    let cancelled = false;
    loginPageContentService
      .list()
      .then((rows) => {
        if (!cancelled) setContents((rows as unknown as LoginContent[]) ?? []);
      })
      .catch(() => {
        // Network or backend hiccup — leave the marketing column empty rather
        // than blocking sign-in.
        if (!cancelled) setContents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const activeContent = useMemo(
    () =>
      contents.find((c) => c.language === currentLang && c.isActive) ??
      contents.find((c) => c.isActive) ??
      null,
    [contents, currentLang],
  );

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '', keepSignedIn: false },
  });

  const onSubmit = async (values: LoginFormValues) => {
    setIsSubmitting(true);
    try {
      await dispatch(login(values.username.trim(), values.password.trim(), values.keepSignedIn) as any);
    } catch (error: any) {
      errorMessageToast(error?.message ?? 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative grid min-h-screen grid-cols-1 md:grid-cols-12">
      <ThemeFlagBar
        contents={contents}
        currentLang={currentLang}
        onLangChange={(lang) => i18n.changeLanguage(lang)}
      />

      {/* Left — login form */}
      <div className="flex min-h-screen flex-col bg-background p-6 md:col-span-5 md:p-10 lg:p-14">
        <div className="mb-10">
          <img src={LogoLight} alt="SMSReflex" className="h-12 w-auto dark:hidden" />
          <img src={LogoDark} alt="SMSReflex" className="hidden h-12 w-auto dark:block" />
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <p className="mb-6 text-sm text-muted-foreground">
            {t('LOGIN.SIGN_IN_TO_START_YOUR_SESSION')}
          </p>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t('COMMON.USERNAME')}</Label>
              <Input
                id="username"
                required
                autoComplete="username"
                disabled={isSubmitting}
                {...form.register('username')}
              />
              {form.formState.errors.username && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.username.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('COMMON.PASSWORD')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  className="pr-10"
                  {...form.register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {form.formState.errors.password && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="keepSignedIn"
                  checked={form.watch('keepSignedIn')}
                  onCheckedChange={(checked) =>
                    form.setValue('keepSignedIn', checked === true)
                  }
                  disabled={isSubmitting}
                />
                <Label htmlFor="keepSignedIn" className="cursor-pointer text-sm font-normal">
                  {t('LOGIN.REMEMBER_ME')}
                </Label>
              </div>
              <Link
                to="/forgotpassword"
                className="text-sm text-primary hover:underline"
              >
                {t('LOGIN.FORGOT_PASSWORD?')}
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('LOGIN.SIGN_IN')}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              {t('LOGIN.DONT_HAVE_AN_ACCOUNT')}{' '}
              <Link to="/register" className="text-primary hover:underline">
                {t('REGISTRATION.REGISTER')}
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right — marketing zone */}
      <div className="relative hidden flex-col overflow-hidden bg-[#063ea6] p-10 text-white md:col-span-7 md:flex md:p-14 lg:p-20">
        {/* subtle dot pattern */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(white_1px,transparent_1px)] [background-size:24px_24px]"
        />

        <div className="relative flex max-w-2xl flex-1 flex-col justify-center">
          {/* No logo here — the form-side logo (top-left) is the only one,
              we don't want to brand the page twice. */}

          {activeContent?.headline && (
            <h1 className="text-3xl font-bold leading-tight tracking-tight md:text-4xl">
              {activeContent.headline}
            </h1>
          )}
          {activeContent?.subtitle && (
            <p className="mt-4 text-base text-white/80 md:text-lg">
              {activeContent.subtitle}
            </p>
          )}

          {activeContent?.bullets && activeContent.bullets.length > 0 && (
            <ul className="mt-10 space-y-5">
              {activeContent.bullets.map((b, idx) => {
                const Icon = resolveIcon(b.icon);
                return (
                  <li key={idx} className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-white/10 ring-1 ring-white/15">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      {b.title && (
                        <p className="font-semibold leading-snug">{b.title}</p>
                      )}
                      {b.desc && (
                        <p className="mt-1 text-sm text-white/75">{b.desc}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {activeContent?.ctaText && (
            <div className="mt-12">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-[#063ea6] transition-transform hover:scale-[1.02]"
              >
                {activeContent.ctaText}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
